import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, User, Eye, FolderOpen, Archive, TrendingUp, Calendar as CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';

const DashboardFilesList = ({
  fileStatusTab, setFileStatusTab,
  nouveauFileCount, actifFileCount, closFileCount,
  filteredFiles, recentFiles,
}) => {
  const navigate = useNavigate();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <FolderOpen className="h-6 w-6 text-red-600" />
          Gestion des Dossiers
        </h2>
        <Button onClick={() => navigate('/files')} variant="outline" className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50">
          Voir tous les dossiers
          <Eye className="ml-2 h-4 w-4" />
        </Button>
      </div>

      <div className="flex space-x-2 bg-gray-100 p-1.5 rounded-lg w-fit shadow-inner">
        {[
          { key: 'nouveau', label: 'Nouveaux', icon: '🆕', count: nouveauFileCount, color: 'from-indigo-600 to-purple-600' },
          { key: 'actif', label: 'Actifs', icon: '📂', count: actifFileCount, color: 'from-red-600 to-blue-600' },
          { key: 'clos', label: 'Fermes', icon: '✅', count: closFileCount, color: 'from-gray-600 to-gray-700' },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setFileStatusTab(tab.key)}
            className={`px-4 py-2 rounded-md text-sm font-semibold transition-all duration-200 flex items-center gap-2 ${
              fileStatusTab === tab.key
                ? `bg-gradient-to-r ${tab.color} text-white shadow-lg scale-105`
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
            data-testid={`dashboard-${tab.key}-tab`}
          >
            <span>{tab.icon} {tab.label}</span>
            <Badge className={fileStatusTab === tab.key ? 'bg-white/20 text-white border-white/30' : 'bg-gray-200 text-gray-700'}>
              {tab.count}
            </Badge>
          </button>
        ))}
      </div>

      {filteredFiles.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {recentFiles.map((file) => (
            <Card key={file.id} className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300 cursor-pointer" onClick={() => navigate(`/files/${file.id}`)} data-testid={`dashboard-file-card-${file.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-blue-600" />
                    <h3 className="font-semibold text-gray-900 text-sm">{file.file_number}</h3>
                  </div>
                  <Badge className={`text-xs ${
                    {'ferme':'bg-gray-100 text-gray-700','nouveau':'bg-indigo-100 text-indigo-700','en_cours':'bg-blue-100 text-blue-700'}[file.status] || 'bg-green-100 text-green-700'
                  }`}>
                    {{'nouveau':'Nouveau','en_cours':'En cours','en_traitement':'En traitement','ferme':'Ferme','soumission_ircc':'Soumission IRCC'}[file.status] || 'Ouvert'}
                  </Badge>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center text-gray-600 text-sm">
                    <User className="h-3 w-3 mr-2" />
                    <span className="truncate">{file.client_name}</span>
                  </div>
                  <div className="flex items-center text-gray-500 text-xs">
                    <CalendarIcon className="h-3 w-3 mr-2" />
                    <span>{format(new Date(file.created_at), 'dd/MM/yyyy')}</span>
                  </div>
                </div>
                <Button variant="outline" size="sm" className="w-full mt-3 text-xs" onClick={(e) => { e.stopPropagation(); navigate(`/files/${file.id}`); }}>
                  <Eye className="mr-1 h-3 w-3" /> Voir details
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-0 shadow-lg">
          <CardContent className="p-12 text-center text-gray-500">
            <Archive className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-semibold mb-2">
              {fileStatusTab === 'nouveau' ? 'Aucun nouveau dossier' : fileStatusTab === 'actif' ? 'Aucun dossier actif' : 'Aucun dossier ferme'}
            </p>
          </CardContent>
        </Card>
      )}

      {filteredFiles.length > 6 && (
        <div className="text-center">
          <Button onClick={() => navigate('/files')} variant="outline" className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50">
            Voir les {filteredFiles.length - 6} autres dossiers
            <TrendingUp className="ml-2 h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default DashboardFilesList;
